import { useCallback } from "react";
import { usePreviewStore } from "./previewStore";
import { usePreviewBlockStore } from "./previewBlockStore";

/**
 * Guard for write actions while a read-only "preview as role" session is active.
 *
 * The preview chrome shows buttons using the previewed role's FULL capability
 * set (so an OWNER/ADMIN testing a role sees exactly what that role sees), but
 * the backend token is masked read-only. Without a guard, clicking a write
 * button (e.g. "Gast erstellen" while previewing as Admin) fires a request that
 * the masked token 403s, surfacing the generic "Du hast keine Berechtigung"
 * message — which is misleading: the role CAN do it; the *preview* is just
 * read-only.
 *
 * Wrap a write control's handler with `guard(...)`. While previewing, the
 * guarded action does NOT run — instead the read-only explanation dialog opens
 * ({@link PreviewReadOnlyDialog}). Outside preview the action runs untouched.
 *
 * Because the visible button always belongs to a capability the previewed role
 * genuinely has (chrome = full caps), intercepting at the click is unambiguous:
 * a guarded write in preview is ALWAYS the read-only mask, never a real
 * permission gap.
 */
export function usePreviewWriteGuard() {
  const previewActive = usePreviewStore((s) => s.active);
  const show = usePreviewBlockStore((s) => s.show);

  // Variadic so it's a safe drop-in for ANY handler shape — args (e.g. the
  // MouseEvent, or a dropdown item) are forwarded untouched when not previewing,
  // so wrapping never changes behavior outside preview.
  const guard = useCallback(
    <A extends unknown[]>(action: (...args: A) => void) =>
      (...args: A) => {
        if (previewActive) {
          show();
          return;
        }
        action(...args);
      },
    [previewActive, show],
  );

  // Imperative form for handlers that already branch internally (e.g. a switch
  // dispatcher): returns true and opens the notice when previewing, so the
  // caller can early-return. Returns false (do nothing) outside preview.
  const blockIfPreviewing = useCallback((): boolean => {
    if (previewActive) {
      show();
      return true;
    }
    return false;
  }, [previewActive, show]);

  return { previewActive, guard, blockIfPreviewing };
}
